import { FC } from "react";
import {
  Box,
  Typography,
  Drawer,
  Button,
  IconButton,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore,
  Close,
} from "@mui/icons-material";
import { tokens, labelStyle } from "@/tokens";
import {
  getAuditStatusColor,
  getAuditStatusLabel,
  getAuditStatusIcon,
  getSeverityColor,
  getSeverityIcon,
  getCategoryLabel,
  getDocumentTypeFullLabel,
} from "@/utils/customs-helpers";
import { getEffectiveAuditStatus } from "@/utils/auditSeverity";

interface AuditDetailModalProps {
  open: boolean;
  onClose: () => void;
  audit: ClusterAudit | null;
  loading?: boolean;
  entryNumber?: string;
}

const AuditDetailModal: FC<AuditDetailModalProps> = ({
  open,
  onClose,
  audit,
  loading = false,
  entryNumber,
}) => {
  const effectiveStatus = getEffectiveAuditStatus(audit);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
    >
      <Box sx={{ width: { xs: "100vw", sm: 600 }, maxWidth: "100vw", height: "100%", display: "flex", flexDirection: "column", bgcolor: tokens.bg.surface }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
              p: 3,
            }}
          >
            <CircularProgress size={28} sx={{ color: tokens.brand.primary }} />
          </Box>
        ) : audit ? (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${tokens.border.default}`,
                px: 3,
                py: 1.5,
                bgcolor: tokens.bg.hover,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2, color: tokens.text.primary }}>
                  Audit Results
                </Typography>
                <Chip
                  icon={getAuditStatusIcon(effectiveStatus, 16) || undefined}
                  label={getAuditStatusLabel(effectiveStatus ?? audit.status)}
                  color={getAuditStatusColor(effectiveStatus ?? audit.status)}
                  size="small"
                  sx={{ height: 24, fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold }}
                />
                {audit.confidenceScore !== undefined && (
                  <Chip
                    label={`${audit.confidenceScore}% confidence`}
                    size="small"
                    sx={{ bgcolor: tokens.bg.muted, height: 24, fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold }}
                  />
                )}
              </Box>
              <IconButton onClick={onClose} size="small" sx={{ color: tokens.text.muted }}>
                <Close sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflow: "auto" }}>
              {/* Entry Info */}
              <Box sx={{ px: 3, pt: 2.5, pb: 2, mb: 0 }}>
                <Typography sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary }}>
                  {audit.entryNumber || entryNumber || "N/A"}
                </Typography>
                <Typography sx={{ fontSize: tokens.fontSize.base, color: tokens.text.secondary, mt: 0.25 }}>
                  Entry Number
                </Typography>
              </Box>

              {/* Data Completeness Warning */}
              {audit.dataCompleteness === "LIMITED" && (
                <Box sx={{ px: 3, pb: 2 }}>
                  <Alert severity="warning" sx={{ borderRadius: tokens.radius.md, fontSize: tokens.fontSize.base }}>
                    <Typography sx={{ fontSize: tokens.fontSize.base }}>
                      <strong>Limited Analysis:</strong> No ACE trade data was found
                      for this entry. The audit is based on document
                      cross-referencing only.
                    </Typography>
                  </Alert>
                </Box>
              )}

              {/* Metadata Row */}
              <Box sx={{ px: 3, pb: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 2,
                  border: `1px solid ${tokens.border.default}`,
                  borderRadius: tokens.radius.lg,
                  p: 2,
                }}
              >
                <Box>
                  <Typography sx={labelStyle}>
                    Documents Analyzed
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.book, color: tokens.text.body, mt: 0.25 }}>
                    {audit.documentsAnalyzed || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={labelStyle}>
                    Trade Data Lines
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.book, color: tokens.text.body, mt: 0.25 }}>
                    {audit.tradeDataLinesAnalyzed || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={labelStyle}>
                    Processing Time
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.book, color: tokens.text.body, mt: 0.25 }}>
                    {audit.processingTimeMs
                      ? `${(audit.processingTimeMs / 1000).toFixed(1)}s`
                      : "N/A"}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={labelStyle}>
                    Model
                  </Typography>
                  <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.book, color: tokens.text.body, mt: 0.25 }}>
                    {audit.modelUsed || "N/A"}
                  </Typography>
                </Box>
                {audit.createdAt && (
                  <Box>
                    <Typography sx={labelStyle}>
                      Audited
                    </Typography>
                    <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.book, color: tokens.text.body, mt: 0.25 }}>
                      {new Date(audit.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>
                )}
                {audit.updatedAt && (
                  <Box>
                    <Typography sx={labelStyle}>
                      Last Updated
                    </Typography>
                    <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.book, color: tokens.text.body, mt: 0.25 }}>
                      {new Date(audit.updatedAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
              </Box>

              {/* Discrepancies */}
              {audit.discrepancies && audit.discrepancies.length > 0 ? (
                <Box sx={{ mb: 2.5, px: 3 }}>
                  <Typography
                    sx={{ fontWeight: tokens.fontWeight.bold, fontSize: tokens.fontSize.sm2, color: tokens.text.primary, mb: 1.5 }}
                  >
                    Discrepancies ({audit.discrepancies.length})
                  </Typography>
                  {audit.discrepancies.map((discrepancy, idx) => (
                    <Accordion
                      key={discrepancy.id || idx}
                      defaultExpanded={idx === 0}
                      sx={{
                        border: `1px solid ${getSeverityColor(discrepancy.severity)}30`,
                        borderRadius: `${tokens.radius.md} !important`,
                        mb: 1.5,
                        "&:before": { display: "none" },
                        boxShadow: "none",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMore />}
                        sx={{
                          bgcolor: `${getSeverityColor(discrepancy.severity)}08`,
                          borderRadius: tokens.radius.md,
                          minHeight: "44px !important",
                          "& .MuiAccordionSummary-content": { my: "8px !important" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            flexWrap: "wrap",
                          }}
                        >
                          {getSeverityIcon(discrepancy.severity)}
                          <Chip
                            label={discrepancy.severity}
                            size="small"
                            sx={{
                              bgcolor: `${getSeverityColor(discrepancy.severity)}20`,
                              color: getSeverityColor(discrepancy.severity),
                              fontWeight: tokens.fontWeight.bold,
                              fontSize: tokens.fontSize["2xs"],
                              height: "20px",
                            }}
                          />
                          <Chip
                            label={getCategoryLabel(discrepancy.category)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: tokens.fontSize["2xs"], height: "20px" }}
                          />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: tokens.fontWeight.semibold, flex: 1, fontSize: tokens.fontSize.sm2 }}
                          >
                            {discrepancy.title}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 2 }}>
                        {/* Description */}
                        <Typography
                          variant="body2"
                          sx={{ mb: 2, color: tokens.text.body }}
                        >
                          {discrepancy.description}
                        </Typography>

                        {/* Citations */}
                        {discrepancy.citations &&
                          discrepancy.citations.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: tokens.text.secondary,
                                  fontWeight: tokens.fontWeight.bold,
                                  mb: 1,
                                  display: "block",
                                }}
                              >
                                Sources:
                              </Typography>
                              {discrepancy.citations.map((citation, cidx) => (
                                <Box
                                  key={cidx}
                                  sx={{
                                    p: 1.5,
                                    mb: 1,
                                    bgcolor: tokens.bg.hover,
                                    borderRadius: tokens.radius.sm,
                                    borderLeft: `3px solid ${tokens.brand.primary}`,
                                  }}
                                >
                                  <Box
                                    sx={{ display: "flex", gap: 1, mb: 0.5 }}
                                  >
                                    <Chip
                                      label={
                                        citation.documentName || "Document"
                                      }
                                      size="small"
                                      sx={{ fontSize: tokens.fontSize["2xs"], height: "18px" }}
                                    />
                                    {citation.documentType && (
                                      <Chip
                                        label={getDocumentTypeFullLabel(
                                          citation.documentType as CustomsDocumentType
                                        )}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          fontSize: tokens.fontSize["2xs"],
                                          height: "18px",
                                        }}
                                      />
                                    )}
                                    {citation.tradeDataLine && (
                                      <Chip
                                        label={`Line: ${citation.tradeDataLine}`}
                                        size="small"
                                        sx={{
                                          fontSize: tokens.fontSize["2xs"],
                                          height: "18px",
                                          bgcolor: `${tokens.status.info}15`,
                                          color: tokens.status.info,
                                        }}
                                      />
                                    )}
                                  </Box>
                                  {citation.excerpt && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: "block",
                                        color: tokens.text.excerpt,
                                        fontStyle: "italic",
                                        mt: 0.5,
                                      }}
                                    >
                                      "{citation.excerpt}"
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}

                        {/* Recommendation */}
                        {discrepancy.recommendation && (
                          <Box
                            sx={{
                              p: 1.5,
                              bgcolor: tokens.status.successBg,
                              borderRadius: tokens.radius.md,
                              border: `1px solid ${tokens.status.success}30`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: tokens.chip.green.text, fontWeight: tokens.fontWeight.bold }}
                            >
                              Recommendation:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: tokens.chip.green.text, mt: 0.5, fontSize: tokens.fontSize.sm2 }}
                            >
                              {discrepancy.recommendation}
                            </Typography>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <Box sx={{ px: 3, mb: 2.5 }}>
                  <Alert severity="success" sx={{ borderRadius: tokens.radius.md }}>
                    <Typography sx={{ fontSize: tokens.fontSize.base }}>
                      No discrepancies found. All documents appear to be consistent
                      with the trade data.
                    </Typography>
                  </Alert>
                </Box>
              )}

              {/* Analysis Text */}
              {audit.analysisText && (
                <Box sx={{ px: 3, mb: 2 }}>
                  <Accordion
                    sx={{
                      border: `1px solid ${tokens.border.default}`,
                      borderRadius: `${tokens.radius.lg} !important`,
                      "&:before": { display: "none" },
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>
                        Full Analysis
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography
                        sx={{
                          fontSize: tokens.fontSize.sm2,
                          fontWeight: tokens.fontWeight.book,
                          color: tokens.text.body,
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.7,
                        }}
                      >
                        {audit.analysisText}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}

              {/* Thinking Trail */}
              {audit.thinkingTrail && audit.thinkingTrail.length > 0 && (
                <Box sx={{ px: 3, mb: 2 }}>
                  <Accordion
                    sx={{
                      border: `1px solid ${tokens.border.default}`,
                      borderRadius: `${tokens.radius.lg} !important`,
                      "&:before": { display: "none" },
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.bold, color: tokens.text.body }}>
                        Reasoning Steps ({audit.thinkingTrail.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box component="ol" sx={{ pl: 2, m: 0 }}>
                        {audit.thinkingTrail.map((step, idx) => (
                          <Box
                            component="li"
                            key={idx}
                            sx={{ mb: 1, color: tokens.text.secondary, fontSize: tokens.fontSize.xs }}
                          >
                            {step}
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${tokens.border.default}`, display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={onClose}
                variant="outlined"
                sx={{
                  fontWeight: tokens.fontWeight.semibold,
                  fontSize: tokens.fontSize.sm2,
                  borderRadius: tokens.radius.md,
                  px: 3,
                  py: 0.75,
                  color: tokens.text.body,
                  borderColor: tokens.border.default,
                  "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
                }}
              >
                Close
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ borderRadius: tokens.radius.md, mb: 2 }}>
              No audit data available for this entry.
            </Alert>
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                fontWeight: tokens.fontWeight.semibold,
                fontSize: tokens.fontSize.sm2,
                borderRadius: tokens.radius.md,
                px: 3,
                py: 0.75,
                color: tokens.text.body,
                borderColor: tokens.border.default,
                "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover },
              }}
            >
              Close
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default AuditDetailModal;
